import { useEffect, useState } from 'react';
import { Clock, AlertTriangle } from 'lucide-react';

// Сколько осталось до конца доступа — пробного или оплаченного периода. Дату
// (`endsAt`) и состояние (`accessState`) считает бэкенд той же формулой, что и
// блокировка, поэтому список, карточка и реальный доступ не расходятся.
// Перерасчёт раз в 30с — для UI этого хватает, нагрузка околонулевая.
type Variant = 'compact' | 'full';

export type AccessCountdownProps = {
  endsAt: string | null | undefined;
  variant?: Variant;
  // Что за период — от этого зависят подписи («до конца триала» / «оплачено до»).
  subject?: 'trial' | 'paid';
  // Начало отсчёта прогресс-бара: начало периода или дата регистрации.
  startedAt?: string | null;
  // Из бэкенда: 'grace' — срок прошёл, но доступ ещё держится; 'blocked' — закрыт.
  accessState?: 'ok' | 'grace' | 'blocked' | null;
  graceDaysLeft?: number;
  className?: string;
};

export function pluralDays(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return 'день';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'дня';
  return 'дней';
}

export default function AccessCountdown({
  endsAt,
  variant = 'compact',
  subject = 'trial',
  startedAt,
  accessState,
  graceDaysLeft = 0,
  className,
}: AccessCountdownProps) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(id);
  }, []);

  if (!endsAt) return null;

  const end = new Date(endsAt).getTime();
  const start = startedAt ? new Date(startedAt).getTime() : end - 30 * 86400_000;
  const totalMs = Math.max(end - start, 1);
  const remainingMs = end - now;
  const elapsedMs = Math.max(0, Math.min(totalMs, now - start));
  const progress = Math.max(0, Math.min(1, elapsedMs / totalMs));

  const expired = remainingMs <= 0;
  const days = Math.floor(Math.abs(remainingMs) / 86400_000);
  const hours = Math.floor((Math.abs(remainingMs) % 86400_000) / 3_600_000);
  const minutes = Math.floor((Math.abs(remainingMs) % 3_600_000) / 60_000);
  const periodWord = subject === 'trial' ? 'триала' : 'оплаченного периода';

  const tone: 'green' | 'amber' | 'red' =
    expired || remainingMs < 86400_000 ? 'red' : remainingMs < 7 * 86400_000 ? 'amber' : 'green';

  const compactLabel = expired
    ? accessState === 'blocked'
      ? 'Доступ закрыт'
      : accessState === 'grace'
        ? `Просрочен · ${graceDaysLeft} ${pluralDays(graceDaysLeft)} до отключения`
        : days > 0
          ? `Истёк ${days} ${pluralDays(days)} назад`
          : 'Истекает сейчас'
    : days >= 2
      ? `${days} ${pluralDays(days)}`
      : days >= 1
        ? `${days} дн ${hours} ч`
        : hours >= 1
          ? `${hours} ч ${minutes} м`
          : `${Math.max(0, minutes)} мин`;

  if (variant === 'compact') {
    return (
      <span
        className={`inline-flex items-center gap-1.5 text-[12px] font-medium ${toneText(tone)} ${className ?? ''}`}
        title={`${subject === 'trial' ? 'Триал' : 'Оплачено'} до ${new Date(endsAt).toLocaleString('ru-RU')}`}
      >
        <span className={`h-1.5 w-1.5 rounded-full ${toneDot(tone)}`} />
        {compactLabel}
      </span>
    );
  }

  // full variant
  const longLabel = expired
    ? accessState === 'blocked'
      ? 'Доступ закрыт за неоплату'
      : accessState === 'grace'
        ? `Просрочен · доступ закроется через ${graceDaysLeft} ${pluralDays(graceDaysLeft)}`
        : `${subject === 'trial' ? 'Триал' : 'Период'} завершён ${days} ${pluralDays(days)} назад`
    : days >= 2
      ? `${days} ${pluralDays(days)} до конца ${periodWord}`
      : days >= 1
        ? `${days} дн ${hours} ч до конца`
        : hours >= 1
          ? `${hours} ч ${minutes} мин до конца`
          : `${Math.max(0, minutes)} мин до конца`;

  return (
    <div className={`border ${toneBorder(tone)} ${toneBg(tone)} px-4 py-3 ${className ?? ''}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          {expired ? (
            <AlertTriangle size={14} strokeWidth={1.75} className={toneIcon(tone)} />
          ) : (
            <Clock size={14} strokeWidth={1.75} className={toneIcon(tone)} />
          )}
          <span className={`text-[13px] font-medium ${toneText(tone)}`}>{longLabel}</span>
        </div>
        <span className={`font-mono text-[11px] ${toneText(tone)} opacity-70`}>
          до {new Date(endsAt).toLocaleDateString('ru-RU')}
        </span>
      </div>
      <div className="mt-2 h-1 w-full bg-ink/10">
        <div
          className={`h-full ${toneFill(tone)}`}
          style={{ width: `${Math.round(progress * 100)}%` }}
        />
      </div>
    </div>
  );
}

function toneText(t: 'green' | 'amber' | 'red'): string {
  return t === 'green' ? 'text-green-700' : t === 'amber' ? 'text-amber-700' : 'text-red-700';
}
function toneIcon(t: 'green' | 'amber' | 'red'): string {
  return t === 'green' ? 'text-green-600' : t === 'amber' ? 'text-amber-600' : 'text-red-600';
}
function toneDot(t: 'green' | 'amber' | 'red'): string {
  return t === 'green' ? 'bg-green-600' : t === 'amber' ? 'bg-amber-500' : 'bg-red-600';
}
function toneFill(t: 'green' | 'amber' | 'red'): string {
  return t === 'green' ? 'bg-green-600' : t === 'amber' ? 'bg-amber-500' : 'bg-red-600';
}
function toneBorder(t: 'green' | 'amber' | 'red'): string {
  return t === 'green'
    ? 'border-green-600/30'
    : t === 'amber'
      ? 'border-amber-500/40'
      : 'border-red-600/40';
}
function toneBg(t: 'green' | 'amber' | 'red'): string {
  return t === 'green' ? 'bg-green-50' : t === 'amber' ? 'bg-amber-50' : 'bg-red-50';
}
